// ============================================================
// MASH evidence renderer — DEV fixture harness (ME-MACHINEID-RENDER SP-6)
// ============================================================
// The PRE-FLIP visual-verification surface. Emit is OFF this milestone
// (`MACHINE_EVIDENCE_RENDER_LIVE`), so there is NO live bot-emitted card to eyeball.
// This hidden, UNLINKED, data-free surface renders the REAL SP-2/SP-4 viewers
// against the canned static fixtures so the user can visually verify on `.cc`
// BEFORE we request the joint flip.
//
// It is mounted on an UNLINKED route (`/midas/_fixtures` in useCustomerRoutes) —
// NOT in any nav/menu. It makes ZERO API calls and reads NOTHING from the URL or
// tenant; it renders ONLY the imported static fixtures. Because `.cc` is a
// production build, the route is reachable there by typing the URL for the visual
// check; it exposes nothing (data-free), so it is safe to exist unlinked in the
// bundle. It does NOT alter any existing customer route.
import React, { ReactNode } from "react";

import {
  ARM_EVIDENCE_ORACLE,
  CHUCK_CLAMP_EVIDENCE_ORACLE,
  FEEDER_MACHINE_CARD,
  OUTPUT_HERO_MACHINE_CARD,
  OVERVIEW_MACHINE_CARD,
  TURN_WINDOW_ARM_CARD,
  WINDOWED_FEEDER_MACHINE_CARD,
} from "../../../../../../shared/components/machineEvidence/__fixtures__";
import { dt } from "../../../../../../shared/themes/redesignTokens";
import { ArtifactTab } from "../../../../midasPageModels";
import MidasArtifactMachineCardV3 from "../MidasArtifactMachineCardV3";
import MidasArtifactMachineEvidenceV3 from "../MidasArtifactMachineEvidenceV3";

const makeCardTab = (id: string, title: string, data: unknown): ArtifactTab => ({
  id,
  artifact: { type: "machine_card", title, source: "bot", url: "", data: data as Record<string, unknown> },
  zoom: 100,
});

const makeEvidenceTab = (id: string, title: string, data: unknown): ArtifactTab => ({
  id,
  artifact: { type: "machine_evidence", title, source: "bot", url: "", data: data as Record<string, unknown> },
  zoom: 100,
});

const Section: React.FC<{ title: string; subtitle: string; children: ReactNode }> = ({
  title,
  subtitle,
  children,
}) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 28 }}>
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ fontFamily: dt.font, fontSize: 15, fontWeight: 600, color: dt.textPrimary }}>{title}</span>
      <span style={{ fontFamily: dt.fontMono, fontSize: 11, color: dt.textMuted }}>{subtitle}</span>
    </div>
    <div
      style={{
        height: 620,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderRadius: 8,
        border: `1px solid ${dt.cardBorder}`,
        background: dt.cardBg,
      }}
    >
      {children}
    </div>
  </div>
);

const MachineEvidenceFixtureHarnessV3: React.FC = () => (
  <div
    style={{
      minHeight: "100vh",
      overflow: "auto",
      padding: "24px 28px",
      background: dt.headerBg,
      fontFamily: dt.font,
    }}
  >
    <div style={{ maxWidth: 720, margin: "0 auto", display: "flex", flexDirection: "column" }}>
      <h1 style={{ fontFamily: dt.font, fontSize: 20, fontWeight: 700, color: dt.textPrimary, marginBottom: 4 }}>
        MASH evidence cards — fixture harness
      </h1>
      <p style={{ fontFamily: dt.fontMono, fontSize: 11, color: dt.textMuted, marginTop: 0, marginBottom: 24 }}>
        Dev-only · unlinked · data-free · static fixtures only (emit gate OFF). Not in any nav.
      </p>

      <Section
        title="machine_card — FEEDER (STALE + historical_backfill)"
        subtitle="MidasArtifactMachineCardV3 · two-clock split, per-bowl breakdown, provenance footer"
      >
        <MidasArtifactMachineCardV3 tab={makeCardTab("fx-feeder", "MASH — Feeders", FEEDER_MACHINE_CARD)} />
      </Section>

      <Section
        title="machine_card — OVERVIEW (six section tabs)"
        subtitle="MidasArtifactMachineCardV3 · worst-verdict roll-up, placeholder tabs"
      >
        <MidasArtifactMachineCardV3 tab={makeCardTab("fx-overview", "MASH — Overview", OVERVIEW_MACHINE_CARD)} />
      </Section>

      <Section
        title="machine_card — WINDOWED FEEDER (5-pill period selector)"
        subtitle="MidasArtifactMachineCardV3 · SP-1b multi-window pills over delivered windows[]"
      >
        <MidasArtifactMachineCardV3
          tab={makeCardTab("fx-windowed", "MASH — Windowed", WINDOWED_FEEDER_MACHINE_CARD)}
        />
      </Section>

      <Section
        title="machine_card — OUTPUT hero (A5 layout vs output-model.PNG)"
        subtitle="ME-CARDS-R1 A5 · big-number hero + rate line with explicit denominator + supporting tiles (SKU / waste / pending uptime) · LIVE freshness detail"
      >
        <MidasArtifactMachineCardV3
          tab={makeCardTab("fx-output-hero", "MASH — Output", OUTPUT_HERO_MACHINE_CARD)}
        />
      </Section>

      <Section
        title="machine_card — TURN-WINDOW ARM (live 22.08. payload shape)"
        subtitle="ME-CARDS-R1 · turn window `yesterday` outside the pinned enum → leading turn pill, primary read rendered; one provenance row for two figures citing one source"
      >
        <MidasArtifactMachineCardV3
          tab={makeCardTab("fx-turn-arm", "MASH — Arm (turn window)", TURN_WINDOW_ARM_CARD)}
        />
      </Section>

      <Section
        title="machine_evidence — ARM oracle"
        subtitle="MidasArtifactMachineEvidenceV3 · hero rate + jaw residual + cold_armed + deep-link (arm)"
      >
        <MidasArtifactMachineEvidenceV3
          tab={makeEvidenceTab("fx-arm", "Arm — reject rate & jaw residual", ARM_EVIDENCE_ORACLE)}
        />
      </Section>

      <Section
        title="machine_evidence — chuck_clamp oracle"
        subtitle="MidasArtifactMachineEvidenceV3 · measure_basis + absent NCK + non-arm deep-link (disabled)"
      >
        <MidasArtifactMachineEvidenceV3
          tab={makeEvidenceTab("fx-chuck", "Chuck clamp — live PLC", CHUCK_CLAMP_EVIDENCE_ORACLE)}
        />
      </Section>
    </div>
  </div>
);

export default MachineEvidenceFixtureHarnessV3;
